import { AskScope } from '../api';
import { cardLinkDirective, noCardLinkDirective } from './cardLinkDirective';
import { languageDirective } from './languageDirective';

/**
 * Directives carry what the client knows and the agent otherwise cannot. The gateway holds
 * the system prompt and receives nothing from us but the prompt text and the scope, so the
 * user's UI language and the channel slug that card links are built from have to ride along
 * in the prompt itself.
 *
 * They are split by surface rather than shared, because the two surfaces want different
 * answers and the prompt budget is shared with the user's own question (see
 * `askPromptLimit`) — an instruction that cannot apply is not free.
 *
 * Only the raw prompt is ever displayed; directives are never shown.
 */

/**
 * Directives for the Ask panel: a conversation whose answers name cards the user will want
 * to open, sometimes in tables, sometimes as HTML.
 */
export function withChatDirectives(prompt: string, scope: AskScope): string {
  return [prompt, cardLinkDirective(scope), languageDirective()].join('\n\n');
}

/**
 * Directives for the summary widgets: the language, plus one line ruling out links.
 *
 * The panel's `cardLinkDirective` cannot land here. A summary is about the one record the
 * user already has open, so the only link it could offer points back at that record, and
 * `AiSummary` renders its markdown without the transcript's link handler, so a link it did
 * produce would leave the app on a full page load rather than routing. Saying nothing is
 * not the same as saying no, though — the gateway's system prompt is not ours and may ask
 * for links on its own — so `noCardLinkDirective` refuses them explicitly, at a fraction
 * of the panel set's cost.
 */
export function withSummaryDirectives(prompt: string): string {
  return [prompt, noCardLinkDirective(), languageDirective()].join('\n\n');
}
